import {
    Component,
    OnInit,
    ViewEncapsulation,
    OnDestroy,
    ChangeDetectionStrategy,
    ViewChild,
    ChangeDetectorRef,
    Inject
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { UsersService } from 'app/pages/admin/users.service';
import { MultiSelectGroupComponent } from '../../../../shared/components/multi-select/multi-select-group/multi-select-group.component';
import { UiUtilsService } from 'app/shared/utils/ui-utils.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'app/environments/environment';
import { BehaviorSubject } from 'rxjs';
import { DailySummaryReportService } from '..';
import { saveAs as importedSaveAs } from 'file-saver';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { VaultService } from 'app/pages/vault';
import { MultiSelectAutoCompleteComponent } from 'app/shared/components/multi-select/multi-select-auto-complete/multi-select-auto-complete.component';
import { customerQueryParam } from 'app/shared/models/customer';
import { DailyReportExport, DailyReportDisplayModel } from 'app/shared/models/daily-summary';
import { EntitySelectorObject, EntitySelectorEntity } from 'app/shared/models/entities';
import { LocationDetailsGroupSelector, LocationGroup, LocationGroupSelector } from 'app/shared/models/location-group';
import { LocationArgs, Locations } from 'app/shared/models/locations';
import { SelectableGroup } from 'app/shared/models/selectable';
import { EntityResponseItem } from 'app/shared/models/view-data';
import { EntityService } from 'app/shared/services/entity.service';
import { SnackBarNotificationService } from 'app/shared/services/snack-bar-notification.service';
import {
    DataExportType,
    DataExportFile,
    LocationType,
    EntityExportType,
    ExportPerSheet,
} from 'app/shared/services/telemetry.service';
import { DomOperationUtilsService } from 'app/shared/utils/dom-operation-utils.service';
import { LocationGroupService } from 'app/shared/services/location-group.service';
import { TrackBy } from 'app/shared/utils/track-by';

export interface DailySummaryExportComponentData {
    preSelectedLocationId: number;
    preSelectedEntityIds: number[];
    allowedEntityGroups: number[];
    startDate: string;
    endDate: string;
    includeInactiveLocations: boolean;
    allLocations: Locations[];
    locationGroups: LocationGroup[];
}

export interface EntitySelectorEntityWithDisabled extends EntitySelectorEntity {
    disabled?: boolean;
}


@Component({
    selector: 'ads-daily-summary-export',
    templateUrl: './daily-summary-export.component.html',
    styleUrls: ['./daily-summary-export.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DailySummaryExportComponent implements OnInit, OnDestroy {
    @ViewChild('multiSelectAutoCompleteLocations')
        private multiSelectAutoCompleteLocations: MultiSelectAutoCompleteComponent;
    @ViewChild('entitiesGroupMultiselect') public entitiesGroupMultiselect: MultiSelectGroupComponent;

    public dailyExport: DailyReportExport = {
        fileType: DataExportType.Excel,
        singleOrMultiple: DataExportFile.Single,
        locationType: LocationType.Location,
        startDate: this.convertDate(
            new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 7, 0, 0, 0).toString(),
        ),
        endDate: this.convertDate(
            new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 1, 0, 0, -1).toString(),
            true,
        ),
    };
    
    public filteredLocations = new Array<SelectableGroup>();
    private subscriptions = new Array<Subscription>();
    public locationTypeArray = new Array<DailyReportDisplayModel>();
    public fileTypeArray = new Array<DailyReportDisplayModel>();
    public csvFileArray = new Array<DailyReportDisplayModel>();
    public entityPerColumnRowArray = new Array<DailyReportDisplayModel>();
    public dataToBeexportedPerSheet = new Array<DailyReportDisplayModel>();
    public isLoading: boolean;
    public selectedLocations = new Array<SelectableGroup>();
    public entityList = new Array<SelectableGroup>();
    public locationGroups = new Array<LocationGroupSelector>();
    public selectedEntitySet = new Set();
    public selectedEntityIds = new Array<number>();
    public savedSettingEntitieIds = [];
    public savedSettingLocationIds = [];
    public exportEntitySelectorObjects: EntitySelectorObject[] = [];
    public isEntitiesInvalid = false;
    public isLocationGroupInvalid = false;
    public locationType: number;
    public showErrorLocLimit: boolean;
    public preSelectedLocationGroup: SelectableGroup;
    public preSelectedLocation = new Array<SelectableGroup>();

    public translations = {
        EXPORT_ERR_SNACKBAR_MSG: '',
        NO_DATA_EXPORT_ERR: '',

        LOCATION: '',
        LOCATION_GROUP: '',
        CSV: '',
        EXCEL: '',
        SINGLE: '',
        MULTIPLE: '',
        ROW: '',
        COLUMN: '',
        ENTITY: '',
        DISMISS_TEXT: ''
    }

    public locationGroupId: string;
    public isValidDateRange = true;
    public isStatusCodeFlagValue = false;
    public showSaveSetting = false;

    public preStartDate: Date;
    public preEndDate: Date;
    public readonly apiUri = environment.serviceUrl;
    public entitySelectorObjects$ = new BehaviorSubject<EntitySelectorObject[]>(undefined);

    public minDate: Date;
    public maxDate: Date;

    public settingText: string;

    public showpageHint: boolean;

    public trackByIndex = TrackBy.byIndex;

    constructor(
        public refDialog: MatDialogRef<DailySummaryExportComponent>,
        private usersService: UsersService,
        @Inject(MAT_DIALOG_DATA) public data: DailySummaryExportComponentData,
        private activatedRoute: ActivatedRoute,
        private entityService: EntityService,
        private uiUtilsService: UiUtilsService,
        private locationGroupService: LocationGroupService,
        private changeDetectorReference: ChangeDetectorRef,
        private translate: TranslateService,
        private snackBarNotificationService: SnackBarNotificationService,
        private dailyReportService: DailySummaryReportService,
        private vaultService: VaultService,
        private domOperationService: DomOperationUtilsService,
    ) {
        this.translations.EXPORT_ERR_SNACKBAR_MSG = this.translate.instant('REPORT.DAILY_SUMMARY.EXPORT.EXPORT_ERR_SNACKBAR_MSG');
        this.translations.NO_DATA_EXPORT_ERR = this.translate.instant('REPORT.DAILY_SUMMARY.EXPORT.NO_DATA_EXPORT_ERR');

        this.translations.LOCATION = this.translate.instant('COMMON.LOCATION');
        this.translations.LOCATION_GROUP = this.translate.instant('COMMON.LOCATION_GROUP');
        this.translations.CSV = this.translate.instant('COMMON.CSV');
        this.translations.EXCEL = this.translate.instant('COMMON.EXCEL');
        this.translations.SINGLE = this.translate.instant('COMMON.SINGLE');
        this.translations.MULTIPLE = this.translate.instant('COMMON.MULTIPLE');
        this.translations.ROW = this.translate.instant('COMMON.ROW');
        this.translations.COLUMN = this.translate.instant('COMMON.COLUMN');
        this.translations.ENTITY = this.translate.instant('COMMON.ENTITY');
        this.translations.DISMISS_TEXT = this.translate.instant('COMMON.DISMISS_TEXT');

        this.dailyExport.startDate = this.convertDate(data.startDate);
        this.dailyExport.endDate = this.convertDate(data.endDate, true);
        this.preStartDate = new Date(data.startDate);
        this.preEndDate = new Date(data.endDate);
    }

    public ngOnInit() {
        const hintSubs = this.domOperationService.showpageHint.subscribe((data) => {
            this.showpageHint = data;
            this.uiUtilsService.safeChangeDetection(this.changeDetectorReference);
        });
        this.subscriptions.push(hintSubs);
        this.isLoading = true;

        this.locationTypeArray = [
            { id: LocationType.Location, value: this.translations.LOCATION },
            { id: LocationType.LocationGroup, value: this.translations.LOCATION_GROUP },
        ];
        this.fileTypeArray = [
            { id: DataExportType.AdsCsv, value: this.translations.CSV },
            { id: DataExportType.Excel, value: this.translations.EXCEL },
        ];
        this.csvFileArray = [
            { id: DataExportFile.Single, value: this.translations.SINGLE },
            { id: DataExportFile.Multiple, value: this.translations.MULTIPLE },
        ];
        this.entityPerColumnRowArray = [
            { id: EntityExportType.Row, value: this.translations.ROW },
            { id: EntityExportType.Column, value: this.translations.COLUMN },
        ];
        this.dataToBeexportedPerSheet = [
            { id: ExportPerSheet.Location, value: this.translations.LOCATION },
            { id: ExportPerSheet.Entity, value: this.translations.ENTITY },
        ];
        this.activatedRoute.queryParamMap.subscribe((params) => {
            this.dailyExport.cid = Number(params.get(customerQueryParam));
        });
        this.locationGroups = this.locationGroupService.globalSelectableLocationGroups;
        this.loadLocations();
        this.entitySelectorObjects$.subscribe(original => {
            this.exportEntitySelectorObjects = original.map(group => ({
                ...group,
                entities: group.entities.map(e => ({ ...e }))
            }));
        });
    }

    public loadLocations() 
    {
        const locationArgs: LocationArgs = {
            customerId: this.dailyExport.cid,
            IncludeInactiveLocations: this.data.includeInactiveLocations,
        };
        this.preSelectedLocation = [];
        const locationSubscription = this.usersService.getLocationsList(locationArgs).subscribe(
            (locations) => {
                if (locations) {
                    locations.forEach((item) => {
                        const isChecked =
                            this.data &&
                            this.data.preSelectedLocationId &&
                            item.locationId === this.data.preSelectedLocationId;
                        this.filteredLocations.push({
                            name: item.locationName,
                            id: item.locationId,
                            isChecked: isChecked,
                        });
                    });
                    this.filteredLocations.sort(function (a, b) {
                        return a.name.localeCompare(b.name);
                    });
                    this.preSelectedLocation = this.filteredLocations.filter((item) => item.isChecked);
                    this.getSelectedEntities({ selectedLocations: this.preSelectedLocation });
                    this.selectedLocations = this.filteredLocations.filter((item) => item.isChecked);
                }

                this.isLoading = false;
                this.uiUtilsService.safeChangeDetection(this.changeDetectorReference);
            },
            () => {},
        );
        this.subscriptions.push(locationSubscription);
    }

    public restrictKeys(event: KeyboardEvent): boolean {
        if (event.keyCode === 222 || event.keyCode === 188 || event.keyCode === 190 || event.keyCode === 220) {
            return false;
        }
        return true;
    }

    public onLocationTypeChange(type : number)
    {
        this.filteredLocations = [];
        this.locationType = type;
        if(!this.locationType)
            this.loadLocations();
    }

    public handleSelectedLocationsGroup(locationGroup: LocationDetailsGroupSelector[]): void {
        this.showErrorLocLimit = false;
        this.isLocationGroupInvalid = !(locationGroup.length > 0);
        if (locationGroup.length > 0) {
            this.isLoading = true;
            this.locationGroupId = locationGroup[0].id.toString() ?? null;

            const selectedGroup = locationGroup[0];
            const groupLocationIds = selectedGroup.locations;
            const groupLocationIdSet = new Set(groupLocationIds.map(a => a.locationID));

            const locationsInGroup = this.data.allLocations.filter(loc =>
                groupLocationIdSet.has(loc.locationId)
            );

            if(this.locationGroupId == '0')
            {
                this.filteredLocations = this.data.allLocations.map(loc => ({
                id: loc.locationId,
                name: loc.locationName,
                isChecked: false
                }));
            }
            else
            {       
                this.filteredLocations = locationsInGroup.map(loc => ({
                id: loc.locationId,
                name: loc.locationName,
                isChecked: false
                }));
            }

            const fewLoc = this.filteredLocations.length < 50 ? this.filteredLocations : [];
            fewLoc?.forEach(loc => loc.isChecked = true);
            this.preSelectedLocation = [...fewLoc];
            this.selectedLocations = [...fewLoc];

            this.savedSettingEntitieIds = [];
            this.getSelectedEntities({ locationGroupId: Number.parseInt(this.locationGroupId, 10) });
        }
    }

    public handleSelectedLocations(locations: Array<SelectableGroup>): void {
        this.selectedLocations = [];
        this.isLoading = true;
        this.selectedLocations = locations.filter((location: SelectableGroup) => location.isChecked);

        this.showErrorLocLimit = this.locationType == 1 && locations.filter(a => a.isChecked).length == 50;

        if (this.selectedLocations.length > 0) {
            this.savedSettingEntitieIds = [];
            this.getSelectedEntities({ selectedLocations: this.selectedLocations });
        } else {
            this.isLoading = false;
            this.uiUtilsService.safeChangeDetection(this.changeDetectorReference);
        }
    }

   public handleSelectedEntities(entities: EntitySelectorEntity[]) {
    const selected = entities.slice(0, 4);
    this.selectedEntityIds = selected.map(e => e.id);

    const limitReached = selected.length >= 4;

    this.exportEntitySelectorObjects.forEach(group => {
        group.entities.forEach((ent: EntitySelectorEntityWithDisabled) => {
            ent.disabled = limitReached && !selected.some(s => s.id === ent.id);
        });
    });
    this.isEntitiesInvalid = !(this.selectedEntityIds.length > 0);
    this.uiUtilsService.safeChangeDetection(this.changeDetectorReference);
}


    public getSelectedEntities({
        selectedLocations,
        locationGroupId,
    }: {
        selectedLocations?: SelectableGroup[];
        locationGroupId?: number;
    }) {
        if (selectedLocations && selectedLocations.length === 0) {
            return;
        }
        this.selectedEntityIds = [];
        const entitiesSubscription = this.entityService
            .getEntitiesByLocation(
                this.dailyExport.cid,
                selectedLocations ? selectedLocations.map(({ id }) => id) : undefined,
                locationGroupId,
            )
            .subscribe(
                (entities: Array<EntityResponseItem>) => {
                    this.isLoading = false;
                    const filteredEntities = new Array<SelectableGroup>();
                    this.uiUtilsService.safeChangeDetection(this.changeDetectorReference);
                    entities.forEach((entity: EntityResponseItem) => {
                        if (
                            !this.data ||
                            !this.data.allowedEntityGroups ||
                            this.data.allowedEntityGroups.includes(entity.displayGroup)
                        ) {
                            filteredEntities.push({
                                name: entity.entityName,
                                id: entity.entityId,
                                groupName: entity.displayGroupName,
                                groupId: entity.displayGroup,
                            });
                        }
                    });
                    const entitySelectorObjects =
                        this.entityService.vaultSelectableToEntitySelectorObject(filteredEntities);
                    this.entitySelectorObjects$.next(entitySelectorObjects);
                    this.entityList = filteredEntities;

                let initialSelectedIds =
                    this.data && this.data.preSelectedEntityIds
                        ? this.data.preSelectedEntityIds
                        : this.entityList.filter((item) => item.isChecked).map((entity) => entity.id);

                initialSelectedIds = initialSelectedIds.slice(0, 4);

                this.handleSelectedEntities(
                    this.entityList.filter(e => initialSelectedIds.includes(e.id))
                );

                this.selectedEntityIds = initialSelectedIds;
                this.uiUtilsService.safeChangeDetection(this.changeDetectorReference);

                },
                () => {
                    this.isLoading = false;
                    this.uiUtilsService.safeChangeDetection(this.changeDetectorReference);
                },
            );
        this.subscriptions.push(entitiesSubscription);
    }

    public ngOnDestroy() {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    public close() {
        this.refDialog.close();
    }

    public locationsInvalid() {
        return (
            this.multiSelectAutoCompleteLocations &&
            this.selectedLocations &&
            this.selectedLocations.length < 1 &&
            this.multiSelectAutoCompleteLocations.searchInput.ngControl.touched
        );
    }

    public validDateRange(event: boolean) {
        this.isValidDateRange = event;
    }

    public selectedStartDate(event: Date) {
        this.dailyExport.startDate = this.convertDate(event.toString());
    }

    public selectedEndDate(event: Date) {
        this.dailyExport.endDate = this.convertDate(event.toString(), true);
    }

    public convertDate(str: string, endTime?: boolean) {
        const date = new Date(str),
            mnth = ('0' + (date.getMonth() + 1)).slice(-2),
            day = ('0' + date.getDate()).slice(-2);
        let res = [date.getFullYear(), mnth, day].join('-');
        if (endTime) {
            res = res + ' 23:59:59';
        } else {
            res = res + ' 00:00:00';
        }
        return res;
    }

    public onExportSubmit() {
    this.isLoading = true;
    this.uiUtilsService.safeChangeDetection(this.changeDetectorReference);

    const entitiesSelected = this.selectedEntityIds;
    let locationSelected = this.selectedLocations.map((location: SelectableGroup) => location.id);
    if (this.dailyExport.locationType === LocationType.Location) {
        this.locationGroupId = '';
    } else {
        locationSelected = [];
    }

    this.dailyReportService
        .export(this.dailyExport, entitiesSelected, locationSelected, this.locationGroupId)
        .subscribe(
            (res) => {
                this.isLoading = false;
                this.uiUtilsService.safeChangeDetection(this.changeDetectorReference);
                this.fileDownload(res);
                this.close();
            },
            () => {
                this.isLoading = false;
                this.uiUtilsService.safeChangeDetection(this.changeDetectorReference);

                this.snackBarNotificationService.raiseNotification(
                    this.translations.EXPORT_ERR_SNACKBAR_MSG,
                    this.translations.DISMISS_TEXT,
                    { panelClass: 'custom-error-snack-bar' },
                    false
                );
            }
        );
}

    public fileDownload(files: string | string[]) {
        if (files && files.length > 0) {
            this.isLoading = true;
            const selectedFiles: string[] = [];

            if (Array.isArray(files)) {
                files.forEach((file) => {
                    selectedFiles.push('/' + file);
                });
            } else {
                selectedFiles.push('/' + files);
            }

            const splitFile = selectedFiles[0].split('/');
            const fileName = selectedFiles.length === 1 ? splitFile.find((x) => x.includes('.')) : 'Files';
            this.isLoading = false;

            this.vaultService.downloadFile(selectedFiles).subscribe((res) => {
                importedSaveAs(res, fileName);
            });
        }
    }

    public saveSetting() {
        this.showSaveSetting = true;
    }

    public closeExportSetting() {
        this.showSaveSetting = false;
    }
}
